import videojs from 'video.js';
import { LogTable } from './logging/log-table.js';
import LoggingIcon from '../../assets/images/receipt_white_24dp.svg';
import './logging.css';

const _loggingWindow = new WeakMap();
const _loggingInterval = new WeakMap();
const _logs = new WeakMap();
const _table = new WeakMap();

/**
 * The Logging module.
 * @member {LoggingPlugin} logging
 * @memberof MediasitePlayer#
 */

/**
 * Configures the visibility of a logging interface that lists various player-related events as they occur.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class LoggingPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);
        _logs.set(this, []);

        const Component = videojs.getComponent('Component');
        const loggingWindow = new Component(player);
        loggingWindow.addClass('mediasite-player__logging-window');
        loggingWindow.addClass('vjs-hidden');

        _loggingWindow.set(this, loggingWindow);
        _loggingInterval.set(this, loggingWindow.setInterval(() => this.#updateUI(), 250));

        const header = loggingWindow.el().appendChild(document.createElement('header'));
        header.textContent = 'Logging';

        const closeButton = header.appendChild(document.createElement('button'));
        closeButton.type = 'button';
        closeButton.textContent = '×';
        closeButton.setAttribute('aria-label', 'Close');
        closeButton.style = 'position:absolute;right:0;cursor:pointer;';
        this.on(closeButton, 'click', () => this.visible = false);

        const table = new LogTable(player, options);
        _table.set(this, table);
        loggingWindow.addChild(table);

        this.on(this.player, 'keydown', (event) => {
            if (['l', 'L'].includes(event.key)) {
                if (event.ctrlKey && event.altKey && event.shiftKey) {
                    this.visible = !this.visible;
                }
            }
        });

        this.on(this.player, 'play', () => this.log('Started playback.'));
        this.on(this.player, 'pause', () => this.log('Paused playback.'));

        player.ready(() => {
            player.layout().addChild(loggingWindow);

            const params = new URLSearchParams(location.search.toLowerCase());
            if (params.get('loadlog') === 'true') {
                this.visible = true;
                this.player.controlbarMenu().addMenuItem('Show log window', { id: this.name, icon: LoggingIcon, callback: () => this.visible = true });
            }
        });
    }

    #updateUI() {
        const logs = _logs.get(this);
        const newLogs = this.player.log.history().filter(log => !logs.includes(log));
        const table = _table.get(this);

        const loggingWindowEl = _loggingWindow.get(this).el();
        const isAtBottom = Math.round(loggingWindowEl.scrollHeight - loggingWindowEl.scrollTop) === loggingWindowEl.clientHeight;
        for (const log of newLogs) {
            logs.push(log);
            const containerEl = _loggingWindow.get(this).el();
            if (containerEl.children.length < Infinity) {
                const source = log[0].split(':').filter(item => !!item).splice(-1)[0].trim();
                const time = new Date(Math.ceil(performance.timing.navigationStart + performance.now()));
                const description = log.reduce((result, current, currentIndex) => {
                    return result + ((currentIndex && typeof current === 'string') ? ` ${current}` : '');
                }, '');
                if (source !== 'VIDEOJS') {
                    table.addLog({ source, description, time });
                }
            }
        }

        if (isAtBottom) {
            loggingWindowEl.scrollTo(0, loggingWindowEl.scrollHeight);
        }
    }

    /**
     * Whether or not the logging window is currently visible.
     * @type {boolean}
     * @example
     * player.logging.visible = true;
     */
    get visible() {
        const loggingWindow = _loggingWindow.get(this);
        return loggingWindow && !loggingWindow.hasClass('vjs-hidden');
    }
    set visible(visible) {
        const loggingWindow = _loggingWindow.get(this);

        if (visible) {
            loggingWindow.removeClass('vjs-hidden');
        }
        else {
            loggingWindow.addClass('vjs-hidden');
        }
    }

    dispose() {
        clearInterval(_loggingInterval.get(this));

        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }
        
        super.dispose();
    }
}

export default LoggingPlugin;
